'use client'

import { useCallback, useReducer, useState } from 'react'
import { GTM_EVENT } from 'src/interfaces/gtm'
import { useGtm, useNotification, useParticipants, useRegistrant, useSeasonOrganization } from './index'
import { Grade } from '@/interfaces/grade'
import { Teacher } from '@/interfaces/teacher'
import { Participant, ParticipantFile, UpdatingDto } from '@/interfaces/participant'
import { GradeController, ParticipantController, TeacherController } from '@/controllers'
import { logger } from '@/lib'
import { User } from '@/interfaces/user'
import { uploadAvatarFile } from '@/helpers/uploadAvatarFile'
import { useRouter } from 'next/navigation'
import { NotificationType } from '@/components/ui'
import { useAnalytics } from './useAnalytics'
import { AnalyticsEventType } from '@/interfaces/analytics'

const ERROR_REASON_MAX_LENGTH = 120

export interface FormValues {
  firstName: string
  lastName: string
  isDisabled?: boolean
  gradeId: number | string
  teacherLastName: string
}

interface ReducerState {
  data: FormValues | null
  isSchool: boolean
}

export type ReducerAction =
  | { type: 'close'; payload: null }
  | { type: 'clear'; payload: null }
  | { type: 'create'; payload: null }
  | { type: ''; payload: FormValues }

export const useParticipantEditing = () => {
  const [gradeList, setGradeList] = useState<Grade[]>([])
  const [teacherList, setTeacherList] = useState<Teacher[]>([])
  const [avatar, setAvatar] = useState<Blob | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>()
  const [isVisibleDuplicateModal, setVisibilityDuplicateModal] = useState<boolean>(false)
  const [duplicateParticipant, setDuplicateParticipant] = useState<Participant>()
  const [duplicateRegistrant, setDuplicateRegistrant] = useState<{
    user: User
  }>()
  const { openNotification, message } = useNotification()
  const router = useRouter()
  const { seasonOrganization } = useSeasonOrganization()
  const { registrant } = useRegistrant()
  const { revalidate: revalidateParticipants, participants: participantList } = useParticipants()
  const { sendDataToGTM } = useGtm()
  const { track } = useAnalytics()
  const participantsCount = participantList.length

  // Client-emitted registration_error (CW2-648 §4.2): fired ONLY when no
  // server response arrived (network error/timeout) or a client-side error
  // prevented submitting — responded errors are recorded server-side, so one
  // failure never produces both.
  const trackRegistrationError = (reason: string) => {
    track(AnalyticsEventType.REGISTRATION_ERROR, {
      step: 'submit',
      reason: String(reason || 'unknown error').slice(0, ERROR_REASON_MAX_LENGTH),
      source: 'client',
    })
  }

  const loadGradeList = useCallback(async (includeTeacherId?: number): Promise<void> => {
    const params: { sort: string; includeTeacherId?: number } = { sort: 'id:asc' }
    if (includeTeacherId) params.includeTeacherId = includeTeacherId
    const { result } = await GradeController.list(params)
    const [error, list] = result
    if (error) {
      logger.error(error)
      return
    }
    setGradeList(list)
  }, [])

  const loadTeacherList = useCallback(async (gradeId: number, includeTeacherId?: number): Promise<void> => {
    if (!gradeId) return
    const params: { gradeId: number; includeTeacherId?: number } = { gradeId }
    if (includeTeacherId) params.includeTeacherId = includeTeacherId
    const { result } = await TeacherController.list(params)
    const [error, list] = result
    if (error) {
      logger.error(error)
      return
    }
    setTeacherList(list.rows)
  }, [])

  const createTeacher = async (lastName: string, gradeId: number) => {
    const {
      result: [error],
    } = await TeacherController.create({ lastName, gradeId })
    if (error) {
      openNotification(error, NotificationType.ERROR)
    } else {
      openNotification(message.teacherCreatedSuccessfully, NotificationType.SUCCESS)
      await loadTeacherList(gradeId)
    }
  }

  const checkDuplicate = async (data: FormValues, isSchool: boolean) => {
    const checkDuplicatePayload = isSchool
      ? {
          firstName: data.firstName,
          lastName: data.lastName,
          teacherLastName: data.teacherLastName,
          gradeId: data.gradeId as number,
        }
      : {
          firstName: data.firstName,
          lastName: data.lastName,
        }
    const {
      result: [error, result],
    } = await ParticipantController.checkDuplicate(checkDuplicatePayload)
    if (error) {
      openNotification(error, NotificationType.ERROR)
      // the registration submit never happened — a client-side error prevented
      // submitting (§4.2); the duplicate-check route is not instrumented
      // server-side, so this is the only record and cannot double-count
      trackRegistrationError('duplicate check failed')
      return
    }
    if (result.hasDuplicate) {
      setVisibilityDuplicateModal(true)
      setDuplicateParticipant(result.participant)
      setDuplicateRegistrant(result.registrant)
    } else {
      await createParticipant(data, isSchool)
    }
  }

  const createParticipant = async (data: FormValues, isSchool: boolean) => {
    // once the registration request has succeeded, a failure in the
    // post-success bookkeeping (list revalidation, navigation) is NOT a
    // registration error — the participant was already created (§4.2)
    let registrationSucceeded = false
    try {
      const [err, files] = await uploadAvatar()
      if (err) {
        openNotification('Fail to load file', NotificationType.ERROR)
        logger.error(err)
        // an avatar failure prevents the registration submit entirely; the
        // upload route is not instrumented server-side, so this is the only
        // record and cannot double-count (§4.2)
        trackRegistrationError('avatar upload failed')
        return
      }
      const participantFiles: ParticipantFile[] = files.map((file) => ({
        ...file,
        type: 'avatar',
      }))
      const createParticipantPayload = isSchool
        ? {
            firstName: data.firstName,
            lastName: data.lastName,
            teacherLastName: data.teacherLastName,
            gradeId: data.gradeId as number,
            files: participantFiles,
          }
        : {
            firstName: data.firstName,
            lastName: data.lastName,
            files: participantFiles,
          }
      const registrationResponse = await ParticipantController.registration(createParticipantPayload)
      const [error] = registrationResponse.result
      if (error) {
        openNotification(error, NotificationType.ERROR)
        // a responded error is recorded server-side (§6.4); only the
        // no-response case is client-emitted
        if (registrationResponse.isNetworkError) {
          trackRegistrationError(`network: ${error}`)
        }
      } else {
        registrationSucceeded = true
        openNotification(message.participantCreatedSuccessfully, NotificationType.SUCCESS)
        sendDataToGTM({ event: GTM_EVENT.PAR_REG_COMPLETE })
        const eventList = [
          GTM_EVENT.PAR_REG_COMPLETE_FIRST,
          GTM_EVENT.PAR_REG_COMPLETE_SECOND,
          GTM_EVENT.PAR_REG_COMPLETE_THIRD,
          GTM_EVENT.PAR_REG_COMPLETE_FOURTH,
          GTM_EVENT.PAR_REG_COMPLETE_FIFTH,
        ]
        if (eventList[participantsCount]) {
          sendDataToGTM({ event: eventList[participantsCount] })
        }
        await revalidateParticipants()
        router.push('/participant/list')
      }
    } catch (error) {
      openNotification(error as string, NotificationType.ERROR)
      logger.error(error)
      if (!registrationSucceeded) {
        trackRegistrationError((error as Error)?.message || String(error))
      }
    }
  }

  const uploadAvatar = async (): Promise<[Error | null, { bucket: string; objectName: string }[]]> => {
    if (avatar === null) {
      return [null, []]
    }

    return uploadAvatarFile(avatar, String(seasonOrganization?.organizationId))
  }

  const onChangeAvatar = ({ blob, url }: { blob: Blob | null; url: string | undefined }): void => {
    setAvatarUrl(url)
    setAvatar(blob)
  }

  const editParticipant = async (participant: Participant, data: FormValues) => {
    try {
      const [err, files] = await uploadAvatar()
      if (err) {
        openNotification('Fail to load file', NotificationType.ERROR)
        logger.error(err)
        return
      }
      const currentAvatar = participant.files.find((file) => file.type === 'avatar')
      const participantFiles: ParticipantFile[] = files.map((file) => {
        const context = { ...file, type: 'avatar' }
        return currentAvatar ? { ...context, id: currentAvatar?.id } : context
      })

      const participantUpdatePayload: UpdatingDto = {
        id: participant.id,
        firstName: data.firstName,
        lastName: data.lastName,
        disabled: data.isDisabled,
        files: participantFiles,
      }

      if (data.teacherLastName && data.gradeId) {
        participantUpdatePayload.teacherLastName = data.teacherLastName
        participantUpdatePayload.gradeId = parseInt(data.gradeId.toString(), 10)
      }

      const {
        result: [error],
      } = await ParticipantController.update(participantUpdatePayload)

      if (error) {
        openNotification(error, NotificationType.ERROR)
      } else {
        openNotification(message.participantUpdatedSuccessfully, NotificationType.SUCCESS)
        await revalidateParticipants()
        router.push(`/participant/single?id=${participant.id}`)
      }
    } catch (error) {
      openNotification(error as string, NotificationType.ERROR)
      logger.error(error)
    }
  }

  const loadParticipant = async (id: number): Promise<Participant | null> => {
    const {
      result: [error, result],
    } = await ParticipantController.single({ id })
    if (error) openNotification(error, NotificationType.ERROR)
    return result || null
  }

  const closeDuplicateModal = () => setVisibilityDuplicateModal(false)

  const [, reducerDispatch] = useReducer(reducer, {
    data: null,
    isSchool: false,
  })

  function reducer(state: ReducerState, action: ReducerAction): ReducerState {
    switch (action.type) {
      case 'close':
        closeDuplicateModal()
        return state
      case 'clear':
        closeDuplicateModal()
        router.push('/participant/list')
        return state
      case 'create':
        ;(async () => {
          await createParticipant(state.data as FormValues, state.isSchool as boolean)
        })()
        return state
      default:
        return {
          ...state,
          data: action.payload,
        }
    }
  }

  return {
    loadGradeList,
    loadTeacherList,
    loadParticipant,
    createTeacher,
    createParticipant,
    editParticipant,
    avatar,
    avatarUrl,
    uploadAvatar,
    onChangeAvatar,
    closeDuplicateModal,
    reducerDispatch,
    checkDuplicate,
    duplicateParticipant,
    duplicateRegistrant,
    registrant,
    isVisibleDuplicateModal,
    teacherList,
    gradeList,
  }
}
